import classNames from 'classnames';
import { navListCustormerWithChat, navListWorkSupport } from 'constants/stringConstants';
import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { NavList } from 'components/Nav/NavList';
import { Button } from 'ui/Button';
import { ReactComponent as IconLeft } from './img/leftArrow.svg';
import { ReactComponent as IconRight } from './img/rightArrow.svg';
import { ReactComponent as IconPlus } from './UserInfo/img/iconPlus.svg';
import logoSvg from 'assets/img/navSvg/logoNew.svg';
import plus from 'assets/img/navSvg/plus.svg';
import { RootState } from 'redux/store';
import styles from './Nav.module.scss';
import { UserInfo } from './UserInfo';
import { useMediaQuery } from '@mantine/hooks';
import { useGetAlertsCastomerQuery } from 'redux/Api/alertsWorkSupportApi/alertsWorkSupportApi';
import { addEvent } from 'redux/webSocket/slice';
import useYandexMetrica from 'hooks/useYandexMetrica';
import { useGetInfoUserQuery } from 'redux/Api/profileApi/ProfileApi';
import { setUserInfo } from 'redux/userInfo/slice';
import { AppTooltip } from 'ui/AppTooltip';

type NavProps = {
  unreadMessagesSupport?: number;
  unreadMessagesOrderChat?: number;
  setOpenNav?: (test: boolean | undefined) => void;
};

export const Nav: FC<NavProps> = ({
  unreadMessagesSupport = 0,
  unreadMessagesOrderChat = 0,
  setOpenNav,
}) => {
  const isMediumWidth =
    useMediaQuery('(min-width: 768px) and (max-width: 1023px)') ?? false;
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const isMediumWidth = window.matchMedia(
        '(min-width: 768px) and (max-width: 1023px)',
      ).matches;

      if (isMediumWidth) {
        return 'true';
      }

      const saved = localStorage.getItem('collapsed');
      if (saved) {
        return saved;
      }
    }

    return 'false';
  });

  useEffect(() => {
    if (setOpenNav) {
      setOpenNav(collapsed === 'true');
    }
  }, [collapsed, setOpenNav]);

  const prevIsMediumWidthRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      prevIsMediumWidthRef.current = window.matchMedia(
        '(min-width: 768px) and (max-width: 1023px)',
      ).matches;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');

    const handleResize = () => {
      const isMediumWidth = mediaQuery.matches;
      const prevIsMediumWidth = prevIsMediumWidthRef.current;

      if (prevIsMediumWidth !== isMediumWidth) {
        prevIsMediumWidthRef.current = isMediumWidth;

        if (isMediumWidth) {
          setCollapsed('true');
        } else {
          const saved = localStorage.getItem('collapsed');
          if (saved) {
            setCollapsed(saved);
          } else {
            setCollapsed('false');
          }
        }
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleResize);
    } else {
      mediaQuery.addListener(handleResize);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleResize);
      } else {
        mediaQuery.removeListener(handleResize);
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  const { name, avatar } = useSelector((state: RootState) => state.authSlice);
  const { pathname } = useLocation();
  const role = useMemo(() => localStorage.getItem('role') || 'worksupport', []);
  const { sendGoal } = useYandexMetrica();

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      if (prev === 'false') {
        localStorage.setItem('collapsed', 'true');

        return 'true';
      } else {
        localStorage.setItem('collapsed', 'false');

        return 'false';
      }
    });
  };

  const dispatch = useDispatch();
  const websocketState = useSelector((state: RootState) => state.websocket);
  const prevWebsocketLengthRef = useRef<number>(websocketState.length);
  if (role !== 'worksupport') {
    const { data: alerts, refetch } = useGetAlertsCastomerQuery();
    useEffect(() => {
      const result = alerts?.data.map((name: any) => name);
      result?.forEach((alert: any) => {
        return dispatch(addEvent({ alert }));
      });
    }, [alerts, dispatch]);

    useEffect(() => {
      const currentLength = websocketState.length;
      const prevLength = prevWebsocketLengthRef.current;

      if (prevLength > 0 && currentLength < prevLength) {
        refetch();
      }

      prevWebsocketLengthRef.current = currentLength;
    }, [websocketState.length, refetch]);
  }

  const isWorksupportRole = () =>
    (localStorage.getItem('role') || '').toLowerCase() === 'worksupport';

  const { data: userData } = useGetInfoUserQuery(undefined, {
    skip: isWorksupportRole(),
  });

  useEffect(() => {
    if (userData) {
      dispatch(setUserInfo(userData));
    }
  }, [userData, dispatch]);

  return (
    <div
      className={classNames(
        styles.root,
        collapsed === 'true' ? styles.mobile : styles.nav,
        !collapsed || (collapsed === 'false' && isMediumWidth) ? styles.navOverlay : '',
        'mantine-visible-from-sm',
      )}>
      {collapsed === 'true' ? (
        <div className={styles.mobile_wrapper}>
          <div className={styles.nav__list}>
            <button className={styles.toggleBtn} onClick={toggleCollapse}>
              <IconRight />
            </button>
            <ul className={styles.mobile_nav}>
              {(role !== 'director' ? navListWorkSupport : navListCustormerWithChat).map(
                (item) => (
                  <Link to={item[2]} key={item[2]}>
                    <AppTooltip
                      color='#0F1E2F'
                      c='#9397A6'
                      position='right'
                      offset={{ mainAxis: 12, crossAxis: 0 }}
                      label={item[0]}
                      style={{
                        fontSize: '16px',
                        lineHeight: '18px',
                        fontWeight: 500,
                      }}>
                      <li
                        className={
                          item[2].length > 1 &&
                          (pathname === item[2] || pathname.startsWith(`${item[2]}/`))
                            ? `${styles.mobile_nav_active} ${styles.mobile_nav_item}`
                            : styles.mobile_nav_item
                        }
                        onClick={() => {
                          if (item?.[2] === '/knowledge') {
                            sendGoal('KnowledgePage');
                          }
                          if (item?.[2] === '/chat') {
                            sendGoal('SupportPage');
                          }
                        }}>
                        <img className={styles.img} src={item[1]} alt={item[0]} />
                      </li>
                    </AppTooltip>
                  </Link>
                ),
              )}
            </ul>
          </div>
        </div>
      ) : (
        <>
          <div style={{ overflow: 'hidden' }}>
            <img className={styles.logo} src={logoSvg} alt='Skilla logo' />
            <button className={styles.toggleBtn} onClick={toggleCollapse}>
              <IconLeft style={{ height: '16px', width: '16px' }} />
            </button>
          </div>
          <div className={styles.nav__list}>
            <NavList
              navItems={
                role === 'worksupport' ? navListWorkSupport : navListCustormerWithChat
              }
              unreadMessagesSupport={unreadMessagesSupport}
              unreadMessagesOrderChat={unreadMessagesOrderChat}
            />
          </div>

          <div className={styles.nav__footer}>
            {role === 'worksupport' ? (
              <Link to='/beneficiar/create'>
                <Button
                  width='24'
                  height='24'
                  className={classNames(
                    styles.nav__footer_item,
                    styles.nav__footer_item_ben,
                  )}
                  icon
                  src={plus}
                  theme='filed'>
                  Заказчик
                </Button>
              </Link>
            ) : (
              <div className={styles.navUser}>
                <UserInfo userData={userData} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
